// Phase 38 (Screen 3) — Manage membership editor. A standalone route/page,
// keyed by contactId, that edits: status/billing-type/roles (this file),
// the running plan (price/cycle/dates + soft-exit Remove), and Plans &
// passes (list + data-driven quick-adds). Reuses the SAME membership +
// contact CRUD used everywhere else (account-detail.tsx, view-plan.tsx) —
// no new endpoints. Does NOT touch account-detail.tsx or member-profile.tsx.
import { useMemo, useState } from "react";
import { useParams, useLocation } from "wouter";
import {
  useGetContact,
  useListAccountMemberships,
  useListInventoryPlans,
  useUpdateAccountMembership,
  useUpdateAccountContact,
  useCreateAccountMembershipBatch,
  getGetContactQueryKey,
  getListAccountMembershipsQueryKey,
  type Membership,
  type InventoryPlan,
  type UpdateMembershipBodyCycleMode,
  type UpdateContactBodyRolesItem,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

function formatINR(n: number): string {
  if (!Number.isFinite(n)) return "—";
  return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n)}`;
}

// Today in IST as 'YYYY-MM-DD' — default exit date for the Remove confirm.
function todayYmd(): string {
  return new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
}

/** Recurring billing-cycle options actually supported by memberships.cycleMode
 * (Monthly/Quarterly/Six_Month/Yearly). The Screen-3 mockup's copy reads
 * "Monthly/Weekly/Daily", but that vocabulary doesn't exist in the schema —
 * the spec file's own disclaimer says pixel/label details are illustrative,
 * not binding. Reuses the same real enum + labels as view-plan.tsx's Billing
 * Cycle selector so the app has one consistent vocabulary. */
type CycleMode = "Monthly" | "Quarterly" | "Six_Month" | "Yearly";
const CYCLE_OPTIONS: { value: CycleMode; label: string }[] = [
  { value: "Monthly", label: "Monthly" },
  { value: "Quarterly", label: "Quarterly" },
  { value: "Six_Month", label: "Half-yearly" },
  { value: "Yearly", label: "Yearly" },
];

/** Mirrors artifacts/api-server/src/lib/membership-cycle-modes.ts's
 * cycleModeForMonths (feedback_cycle_mode_from_duration — derive cycle_mode
 * from the SELECTED duration, never hardcode). Used only for the rare case a
 * data-driven quick-add plan turns out to be Recurring. */
function localCycleModeForMonths(months: number | null | undefined): CycleMode {
  const m = months ?? 1;
  if (m === 3) return "Quarterly";
  if (m === 6) return "Six_Month";
  if (m === 12) return "Yearly";
  return "Monthly";
}

const ROLE_CHIPS: { value: "primary" | "billing" | "user"; label: string }[] = [
  { value: "primary", label: "Primary" },
  { value: "billing", label: "Payment" },
  { value: "user", label: "Member" },
];

/** Small segmented button group — same visual recipe as add-plan.tsx's
 * Recurring/One-time toggle, generalized to any string-literal option set. */
function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  disabled,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange(o.value)}
          className={cn(
            "px-3 py-1.5 text-xs font-medium transition-colors",
            o.value === value
              ? "bg-primary text-primary-foreground"
              : "bg-background text-muted-foreground hover:bg-muted",
            disabled && "cursor-not-allowed opacity-60",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function ManageMembershipPage() {
  const params = useParams<{ id: string }>();
  const contactId = Number(params.id);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: contact,
    isLoading: contactLoading,
    isError: contactError,
  } = useGetContact(contactId, {
    query: { queryKey: getGetContactQueryKey(contactId), enabled: Number.isFinite(contactId) },
  });
  const accountId = contact?.accountId;

  const { data: memberships, isLoading: membershipsLoading } = useListAccountMemberships(
    accountId ?? 0,
    {
      query: {
        queryKey: getListAccountMembershipsQueryKey(accountId ?? 0),
        enabled: !!accountId,
      },
    },
  );
  const { data: plans = [] } = useListInventoryPlans();

  const updateMembership = useUpdateAccountMembership();
  const updateContact = useUpdateAccountContact();
  const createBatch = useCreateAccountMembershipBatch();

  const planById = useMemo(() => {
    const map = new Map<number, InventoryPlan>();
    for (const p of plans) map.set(p.id, p);
    return map;
  }, [plans]);

  // This member's own lines, newest first. The "running plan" the top cards
  // and the Running-plan card key off is the newest ACTIVE line, falling
  // back to the newest line of any status when nothing is active.
  const own = useMemo(
    () =>
      ((memberships ?? []) as Membership[])
        .filter((m) => m.contactId === contactId)
        .sort((a, b) => b.id - a.id),
    [memberships, contactId],
  );
  const runningMembership = own.find((m) => m.status === "active") ?? own[0] ?? null;
  const runningPlan = runningMembership ? planById.get(runningMembership.planId) ?? null : null;
  const isActive = runningMembership?.status === "active";
  const isOneTime = (runningMembership?.cycleMode as string | null | undefined) === "One_Time";

  // ── Running-plan editable state (Task 2) — seeded from runningMembership,
  // only touched by local edits until "Save changes" (mirrors view-plan.tsx's
  // dirty-tracking recipe: state stays null until the operator types, so the
  // effective value always falls back to the persisted membership). ──
  const [priceInput, setPriceInput] = useState<string | null>(null);
  const [cycleModeInput, setCycleModeInput] = useState<CycleMode | null>(null);
  const [startDateInput, setStartDateInput] = useState<string | null>(null);
  const [endDateInput, setEndDateInput] = useState<string | null>(null);
  // Shared soft-exit confirm target — an array so ONE AlertDialog covers both
  // the Running-plan card's single-line Remove (Task 2) and a whole-deal ×
  // on the Plans & passes list (Task 3).
  const [exitTarget, setExitTarget] = useState<Membership[] | null>(null);
  // Exit date for the Remove confirm — reset to today each time it's opened.
  const [exitDate, setExitDate] = useState(todayYmd);
  function openExitConfirm(targets: Membership[]) {
    setExitDate(todayYmd());
    setExitTarget(targets);
  }

  // There is no raw "set price to ₹X" field on UpdateMembershipBody — only
  // additionalDiscountPct, which reprices frozenDiscountedPrice off
  // plan.monthlyPrice and only touches Unpaid/non-overridden/receipt-free
  // periods (RESEARCH §C3). Back-derive the equivalent discount % from the
  // entered price, same recipe view-plan.tsx already uses in reverse.
  const monthlyBase = runningPlan ? Number(runningPlan.monthlyPrice) : 0;
  const lineDiscPct = Number(runningMembership?.lineDiscountPct ?? 0);
  const discBase = monthlyBase * (1 - lineDiscPct / 100);
  const frozenStored = Number(
    runningMembership?.frozenDiscountedPrice ?? runningMembership?.unitPrice ?? monthlyBase,
  );
  const effPrice = priceInput != null ? Number(priceInput) || 0 : frozenStored;
  const effCycleMode: CycleMode =
    cycleModeInput ?? (runningMembership?.cycleMode as CycleMode | null) ?? "Monthly";
  const effStartDate = startDateInput ?? runningMembership?.startDate ?? "";
  const effEndDate = endDateInput ?? runningMembership?.endDate ?? "";

  const runningPlanDirty =
    runningMembership != null &&
    (Math.round(effPrice * 100) !== Math.round(frozenStored * 100) ||
      (!isOneTime &&
        effCycleMode !== ((runningMembership.cycleMode as CycleMode | null) ?? "Monthly")) ||
      (effStartDate || null) !== (runningMembership.startDate ?? null) ||
      (effEndDate || null) !== (runningMembership.endDate ?? null));

  async function handleSaveRunningPlan() {
    if (!runningMembership || !accountId || !runningPlanDirty) return;
    // Clamp 0-100 — same guard view-plan.tsx applies to additionalDiscountPct.
    const nextAddl =
      discBase > 0
        ? Math.min(100, Math.max(0, Math.round((1 - effPrice / discBase) * 10000) / 100))
        : 0;
    try {
      await updateMembership.mutateAsync({
        id: accountId,
        membershipId: runningMembership.id,
        data: {
          additionalDiscountPct: nextAddl,
          // Billing cycle is a recurring-only concept; a One_Time line's
          // cadence was already set by the Billing-type card above and must
          // be preserved here, not overwritten with a recurring value.
          ...(isOneTime ? {} : { cycleMode: effCycleMode }),
          startDate: effStartDate || null,
          endDate: effEndDate || null,
        },
      });
      toast({ title: "Plan updated." });
      queryClient.invalidateQueries({ queryKey: getListAccountMembershipsQueryKey(accountId) });
      setPriceInput(null);
      setCycleModeInput(null);
      setStartDateInput(null);
      setEndDateInput(null);
    } catch {
      toast({ title: "Couldn't update the plan.", variant: "destructive" });
    }
  }

  // Remove = SOFT-EXIT (status:'exited'), NEVER a hard DELETE — DELETE
  // /accounts/:id/memberships/:membershipId CASCADEs and destroys Paid
  // membership_payment_periods (receipt/invoice numbers included). Gated
  // behind the shared AlertDialog confirm below.
  async function confirmExit() {
    if (!exitTarget || !accountId) return;
    try {
      // Sequential (not Promise.all) so the backend's last-active-plan probe
      // sees each prior line already committed — the final line deactivates
      // the member. Stamps the chosen exit date as the end date.
      for (const m of exitTarget) {
        await updateMembership.mutateAsync({
          id: accountId,
          membershipId: m.id,
          data: { status: "exited", endDate: exitDate },
        });
      }
      toast({ title: exitTarget.length > 1 ? "Deal removed." : "Plan removed." });
      queryClient.invalidateQueries({ queryKey: getListAccountMembershipsQueryKey(accountId) });
      setExitTarget(null);
    } catch {
      toast({ title: "Failed to remove.", variant: "destructive" });
    }
  }

  // ── Plans & passes (Task 3): group by quoteGroupId — a deal is ONE
  // "Plan #{first-line id}" row (LOCKED nomenclature, mirrors
  // member-plan-cards.tsx exactly); a solo plan is its own "Plan #{id}" row. ──
  const planGroups = useMemo(() => {
    const map = new Map<string, Membership[]>();
    for (const m of own) {
      const key = m.quoteGroupId ?? `solo-${m.id}`;
      (map.get(key) ?? map.set(key, []).get(key)!).push(m);
    }
    return Array.from(map.entries());
  }, [own]);

  // Data-driven quick-add candidates (Pitfall 3 — "+ Virtual office" doesn't
  // exist in any seeded space; never hardcode a lookup for it). Match the
  // canonical seed planName for Day pass / Meeting room; loose virtual/office
  // substring match for the third slot. Falls back to a generic plan-picker
  // below when none of the three exist in this space.
  const dayPassPlan = plans.find((p) => p.planName === "Day_Pass");
  const meetingRoomPlan = plans.find((p) => p.planName === "Meeting_Room");
  const virtualOfficePlan = plans.find((p) => /virtual|office/i.test(p.planName));
  const quickAddCandidates = [
    dayPassPlan ? { plan: dayPassPlan, label: "Day pass" } : null,
    meetingRoomPlan ? { plan: meetingRoomPlan, label: "Meeting room" } : null,
    virtualOfficePlan ? { plan: virtualOfficePlan, label: "Virtual office" } : null,
  ].filter((x): x is { plan: InventoryPlan; label: string } => x != null);

  const [genericPlanId, setGenericPlanId] = useState<string>("");

  async function quickAddPlan(plan: InventoryPlan) {
    if (!accountId) return;
    const recurring = plan.cadence === "Recurring";
    const duration = recurring ? Math.max(1, plan.durationMonths ?? 1) : null;
    try {
      await createBatch.mutateAsync({
        id: accountId,
        data: {
          contactId,
          startDate: new Date().toISOString().slice(0, 10),
          createProforma: false,
          lines: [
            {
              planId: plan.id,
              quantity: 1,
              durationMonths: duration,
              // Ignored server-side for non-recurring lines; derived from the
              // selected duration (never hardcoded) when recurring —
              // feedback_cycle_mode_from_duration.
              cycleMode: recurring ? localCycleModeForMonths(duration) : undefined,
            },
          ],
        },
      });
      toast({ title: `${plan.planName.replace(/_/g, " ")} added.` });
      queryClient.invalidateQueries({ queryKey: getListAccountMembershipsQueryKey(accountId) });
    } catch {
      toast({ title: "Couldn't add the plan.", variant: "destructive" });
    }
  }

  // ── Membership status: Active/Inactive → memberships.status active/exited ──
  async function handleStatusChange(next: "Active" | "Inactive") {
    if (!runningMembership || !accountId) return;
    const nextStatus = next === "Active" ? "active" : "exited";
    try {
      await updateMembership.mutateAsync({
        id: accountId,
        membershipId: runningMembership.id,
        data: { status: nextStatus },
      });
      toast({
        title: next === "Active" ? "Membership reactivated." : "Membership marked inactive.",
      });
      queryClient.invalidateQueries({ queryKey: getListAccountMembershipsQueryKey(accountId) });
    } catch {
      toast({ title: "Failed to update status.", variant: "destructive" });
    }
  }

  // ── Billing type: Recurring/One-time → cycleMode ──
  async function handleBillingTypeChange(next: "Recurring" | "One-time") {
    if (!runningMembership || !accountId) return;
    const nextOneTime = next === "One-time";
    try {
      await updateMembership.mutateAsync({
        id: accountId,
        membershipId: runningMembership.id,
        data: {
          // EXPLICIT on update, never auto-derived from durationMonths — that
          // derivation only happens at create time (feedback_cycle_mode_from_duration).
          cycleMode: nextOneTime
            ? ("One_Time" as UpdateMembershipBodyCycleMode)
            : localCycleModeForMonths(runningMembership.durationMonths),
          // One-time needs a concrete window end; default to the existing end
          // (or start) date if none set yet — mirrors view-plan.tsx.
          endDate: nextOneTime
            ? (runningMembership.endDate ?? runningMembership.startDate ?? null)
            : null,
        },
      });
      toast({ title: `Billing set to ${next}.` });
      queryClient.invalidateQueries({ queryKey: getListAccountMembershipsQueryKey(accountId) });
    } catch {
      toast({ title: "Failed to update billing type.", variant: "destructive" });
    }
  }

  // ── Roles: Primary/Payment/Member chips → primary/billing/user ──
  async function handleRoleToggle(role: "primary" | "billing" | "user") {
    if (!contact || !accountId) return;
    const has = contact.roles.includes(role);
    const next = has ? contact.roles.filter((r) => r !== role) : [...contact.roles, role];
    if (next.length === 0) {
      toast({ title: "A member needs at least one role.", variant: "destructive" });
      return;
    }
    try {
      await updateContact.mutateAsync({
        id: accountId,
        contactId,
        data: { roles: next as UpdateContactBodyRolesItem[] },
      });
      toast({ title: "Roles updated." });
      queryClient.invalidateQueries({ queryKey: getGetContactQueryKey(contactId) });
    } catch {
      toast({ title: "Failed to update roles.", variant: "destructive" });
    }
  }

  if (!Number.isFinite(contactId)) {
    return <div className="p-6 text-muted-foreground">Invalid member id</div>;
  }

  if (contactLoading || membershipsLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 p-1">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-32 w-full rounded-md" />
        <Skeleton className="h-32 w-full rounded-md" />
      </div>
    );
  }

  if (contactError || !contact) {
    return (
      <div className="mx-auto max-w-3xl p-6 text-center text-muted-foreground">
        Member not found.{" "}
        <button className="text-primary underline" onClick={() => navigate("/members")}>
          Back to Members
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-1">
      <button
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        onClick={() => navigate(`/members/${contactId}`)}
      >
        <ArrowLeft className="h-4 w-4" /> Back to profile
      </button>

      <div className="flex items-center gap-3">
        {contact.photoUrl ? (
          <img
            src={contact.photoUrl}
            alt={contact.fullName}
            className="h-12 w-12 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-base font-semibold text-muted-foreground">
            {contact.fullName.slice(0, 1).toUpperCase()}
          </div>
        )}
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Manage membership
          </p>
          <h1 className="text-xl font-semibold">{contact.fullName}</h1>
        </div>
      </div>

      {!runningMembership ? (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            This member has no plans yet. Add one from the account's Plan tab.
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Membership status</CardTitle>
              <p className="text-xs text-muted-foreground">
                Inactive members keep their history but can't check in.
              </p>
            </CardHeader>
            <CardContent>
              <SegmentedControl
                options={[
                  { value: "Active", label: "Active" },
                  { value: "Inactive", label: "Inactive" },
                ]}
                value={isActive ? "Active" : "Inactive"}
                onChange={handleStatusChange}
                disabled={updateMembership.isPending}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Billing type</CardTitle>
              <p className="text-xs text-muted-foreground">
                Plan auto-renews at the end of every cycle — member stays active until cancelled.
              </p>
            </CardHeader>
            <CardContent>
              <SegmentedControl
                options={[
                  { value: "Recurring", label: "Recurring" },
                  { value: "One-time", label: "One-time" },
                ]}
                value={isOneTime ? "One-time" : "Recurring"}
                onChange={handleBillingTypeChange}
                disabled={updateMembership.isPending}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Roles</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {ROLE_CHIPS.map((r) => {
                  const active = contact.roles.includes(r.value);
                  return (
                    <button
                      key={r.value}
                      type="button"
                      disabled={updateContact.isPending}
                      onClick={() => handleRoleToggle(r.value)}
                      className={cn(
                        "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                        active
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-input bg-background text-muted-foreground hover:bg-muted",
                        updateContact.isPending && "cursor-not-allowed opacity-60",
                      )}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Running plan</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-medium">Plan #{runningMembership.id}</p>
                  <p className="text-xs text-muted-foreground">
                    {runningPlan
                      ? `${runningMembership.quantity && runningMembership.quantity > 1 ? `${runningMembership.quantity}× ` : ""}${runningPlan.planName.replace(/_/g, " ")}`
                      : `PLN-${runningMembership.planId}`}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => openExitConfirm([runningMembership])}
                >
                  Remove
                </Button>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label>Price (₹)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={effPrice}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => setPriceInput(e.target.value)}
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Reprices via the plan's additional discount — only unpaid/future periods
                    change; already-receipted periods keep their billed amount.
                  </p>
                </div>
                {isOneTime ? (
                  <div className="space-y-1">
                    <Label className="text-muted-foreground">Billing cycle</Label>
                    <Input value="One Time" disabled className="disabled:opacity-100" />
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Label>Billing cycle</Label>
                    <Select
                      value={effCycleMode}
                      onValueChange={(v) => setCycleModeInput(v as CycleMode)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CYCLE_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label>Start date</Label>
                  <Input
                    type="date"
                    value={effStartDate}
                    onChange={(e) => setStartDateInput(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label>End date</Label>
                  <Input
                    type="date"
                    value={effEndDate}
                    min={runningMembership.startDate ?? undefined}
                    onChange={(e) => setEndDateInput(e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Plans &amp; passes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="divide-y rounded-md border">
                {planGroups.length === 0 ? (
                  <p className="p-4 text-center text-sm text-muted-foreground">No plans yet.</p>
                ) : (
                  planGroups.map(([groupKey, group]) => {
                    const firstLine = group[0]!;
                    const names = group
                      .map(
                        (m) =>
                          planById.get(m.planId)?.planName.replace(/_/g, " ") ??
                          `PLN-${m.planId}`,
                      )
                      .join(" | ");
                    const allExited = group.every((m) => m.status === "exited");
                    return (
                      <div
                        key={groupKey}
                        className="flex items-center justify-between gap-2 px-3 py-2.5"
                      >
                        <div>
                          <p className="text-sm font-medium">
                            Plan #{firstLine.id}
                            {allExited && (
                              <Badge variant="secondary" className="ml-2 text-[10px]">
                                Removed
                              </Badge>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">{names}</p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={allExited}
                          onClick={() => openExitConfirm(group)}
                          aria-label={`Remove Plan #${firstLine.id}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Data-driven quick-adds (Pitfall 3) — never a hardcoded
                  "Virtual_Office" lookup. Falls back to a generic plan-picker
                  when none of the three candidate plans exist in this space. */}
              {quickAddCandidates.length > 0 ? (
                <div className="flex flex-wrap items-center gap-2">
                  {quickAddCandidates.map(({ plan, label }) => (
                    <Button
                      key={plan.id}
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={createBatch.isPending}
                      onClick={() => quickAddPlan(plan)}
                    >
                      <Plus className="mr-1 h-3.5 w-3.5" /> {label}
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Select value={genericPlanId} onValueChange={setGenericPlanId}>
                    <SelectTrigger className="h-8 w-[220px] text-xs">
                      <SelectValue placeholder="Choose a plan…" />
                    </SelectTrigger>
                    <SelectContent>
                      {plans.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>
                          {p.planName.replace(/_/g, " ")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!genericPlanId || createBatch.isPending}
                    onClick={() => {
                      const plan = planById.get(Number(genericPlanId));
                      if (plan) quickAddPlan(plan);
                      setGenericPlanId("");
                    }}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" /> Add
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => navigate(`/members/${contactId}`)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveRunningPlan}
              disabled={!runningPlanDirty || updateMembership.isPending}
            >
              {updateMembership.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </>
      )}

      {/* Shared soft-exit confirm — Running-plan card's Remove (single line)
          and (Task 3) Plans & passes' × (whole deal) both route here. Always
          PATCH status:'exited', NEVER DELETE — see confirmExit() above. */}
      <AlertDialog
        open={!!exitTarget}
        onOpenChange={(open) => {
          if (!open) setExitTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {exitTarget && exitTarget.length > 1 ? "Remove this deal?" : "Remove this plan?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This marks the plan{exitTarget && exitTarget.length > 1 ? "s" : ""} as exited as of the
              date below. History and any paid receipts/invoices are preserved — this does not delete
              records. The member is marked Deactivated once their last active plan exits.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5 py-1">
            <Label htmlFor="remove-exit-date">Exit date</Label>
            <Input
              id="remove-exit-date"
              type="date"
              value={exitDate}
              max={todayYmd()}
              onChange={(e) => setExitDate(e.target.value)}
              className="h-9 w-[180px]"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateMembership.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmExit();
              }}
              disabled={updateMembership.isPending}
            >
              {updateMembership.isPending ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
